import { useState, useCallback } from "react";
import { Phone, Loader2, X, AlertCircle } from "lucide-react";
import { apiClient } from "../../utils/apiClient";
import "./OtpLoginStepper.css";

interface OtpLoginStepperProps {
  onClose: () => void;
  onLoginSuccess?: () => void;
}

const OtpLoginStepper: React.FC<OtpLoginStepperProps> = ({ onClose, onLoginSuccess }) => {
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getErrorMessage = (err: any): string => {
    // Try to extract error from response
    if (err.response?.data?.detail) {
      const detail = err.response.data.detail;
      // Check if it's a "not found" error and return generic message
      if (detail.includes('not found') || detail.includes('No valid booking')) {
        return "No bookings found. Please create a booking first, then log in to manage your bookings.";
      }
      return detail;
    }
    
    // Handle 404 specifically
    if (err.response?.status === 404) {
      return "No bookings found. Please create a booking first, then log in to manage your bookings.";
    }
    
    // Generic fallback
    if (err instanceof Error) {
      return err.message;
    }
    
    return "Verification failed. Please try again.";
  };

  const handleVerifyMobile = useCallback(async () => {
    if (mobile.length !== 10) return;
    
    setError("");
    setLoading(true);

    try {
      console.log('🔍 Verifying mobile number:', mobile);
      const response = await apiClient.post('/api/auth/verify-mobileNumber', { mobileNumber: mobile });
      
      if (!response) {
        throw new Error('No response received');
      }
      
      const data = response.data;
      
      // Transform user data to match our User interface
      const transformedUser = {
        id: data.user.id,
        email: data.user.email || `${mobile}@phone.com`,
        name: data.user.name,
        mobileNumber: data.user.mobile_number,
        membershipType: data.user.membership_type || 'basic'
      };
      
      // Store authentication data
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('token_type', data.token_type);
      localStorage.setItem('user', JSON.stringify(transformedUser));
      localStorage.setItem('redirectToAccount', 'true');
      
      console.log('✅ Login successful');
      
      // Reload page to trigger AuthContext to read from localStorage
      window.location.reload();
    } catch (err) {
      console.error('❌ Login failed:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [mobile]);

  return (
    <div className="otp-modal-overlay" onClick={onClose}>
      <div className="otp-container" onClick={(e) => e.stopPropagation()}>
        <div className="otp-card">
          <button className="otp-close-btn" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>

          <div className="otp-header">
            <div className="otp-icon">
              <Phone size={26} />
            </div>
            <h3>Welcome Back</h3>
          </div>

          <div className="otp-content">
            <h5>Sign in with Mobile Number</h5>            
            <div className="mobile-input-row">
              <div className="mobile-input">
                <Phone className="input-icon" size={16} />
                <input
                  type="tel"
                  placeholder="Mobile number"
                  value={mobile}
                  onChange={(e) =>
                    setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  disabled={loading}
                  aria-label="Mobile number input"
                />
              </div>

              <button
                className="send-btn"
                onClick={handleVerifyMobile}
                disabled={mobile.length !== 10 || loading}
                aria-label="Verify"
              >
                {loading ? <Loader2 className="spinner" size={16} /> : "Send"}
              </button>
            </div>

            {error && (
              <div className="error-box">
                <div className="error-icon">
                  <AlertCircle size={20} />
                </div>
                <div className="error-content">
                  <p className="error-text">{error}</p>
                </div>
              </div>
            )}

            {loading && (
              <div className="loading-info">
                <Loader2 size={16} className="spinner" />
                <span>Looking for valid booking with provided mobile number...</span>
              </div>
            )}

            <div className="info-section">
              <h6>Why we need this?</h6>
              <ul>
                <li>We verify your mobile number against our active bookings</li>
                <li>Ensures secure access to your coworking space account</li>
                <li>Enables instant login without additional verification steps</li>
              </ul>
            </div>

            <div className="terms-section">
              <p>By signing in, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy/privacy_policy.html">Privacy Policy</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { OtpLoginStepper };
export default OtpLoginStepper;
